import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { Grid, InputLabel, OutlinedInput, Stack, FormHelperText } from "@mui/material";
import { baseURL } from "../../api";
import axios from "axios";
import { toast } from "react-toastify";
import CustomAlert from "../../component/AlertComponent/Alert";
import * as Yup from "yup";
import { Formik } from "formik";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2)
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1)
  }
}));

export default function ForgotPasswordScreen({
  ForgotPasswordScreenIsOpen,
  setForgotPasswordScreenIsOpen
}) {
  const handleClose = () => {
    setForgotPasswordScreenIsOpen(false);
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        aria-labelledby="forgot-password-dialog-title"
        open={ForgotPasswordScreenIsOpen}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="forgot-password-dialog-title">
          Forgot Password
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={theme => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500]
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Formik
            initialValues={{
              email: ""
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .email("Must be a valid email")
                .max(255)
                .required("Email is required")
            })}
            onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
              try {
                // Trim email first
                const trimmedEmail = values.email.trim();
                // Try with original case first, backend might be case-sensitive
                // If that doesn't work, backend should handle normalization
                const payload = { loginId: trimmedEmail };

                axios
                  .post(`${baseURL}/user/forgot/password`, payload, {
                    headers: {
                      mode: "cors",
                      "Access-Control-Allow-Origin": "*",
                      "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
                      "Content-Type": "application/json"
                    }
                  })
                  .then(response => {
                    toast.success(
                      response?.data?.metadata?.msg || "Password reset link has been sent to your email",
                      {
                        position: "top-center"
                      }
                    );
                    handleClose();
                    setStatus({ success: true });
                    setSubmitting(false);
                  })
                  .catch(error => {
                    
                    // Display the error message from backend, or a generic message if none provided
                    const errorMessage =
                      error?.response?.data?.metadata?.msg ||
                      "Failed to send password reset link. Please try again.";
                    
                    CustomAlert("error", errorMessage, {
                      position: "top-center"
                    });
                    setStatus({ success: false });
                    setSubmitting(false);
                  });
              } catch (err) {
                setStatus({ success: false });
                const errorMessage =
                  err?.response?.data?.metadata?.msg ||
                  "An unexpected error occurred. Please try again.";
                CustomAlert("error", errorMessage, {
                  position: "top-center"
                });
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Enter your email address and we&apos;ll send you a link to reset your password.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="email-forgot-password">Email</InputLabel>
                      <OutlinedInput
                        id="email-forgot-password"
                        type="email"
                        name="email"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        fullWidth
                        error={Boolean(touched.email && errors.email)}
                      />
                      {touched.email && errors.email && (
                        <FormHelperText error id="standard-weight-helper-text-email-forgot-password">
                          {errors.email}
                        </FormHelperText>
                      )}
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      disableElevation
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      Send Password Reset Link
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
}

